import type { ArBillingNote } from '@dcc-erp/shared';
import { fmt, fmtDate, toNumber } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

// Router-free AR billing-note (ใบวางบิล) list. The route owns data fetching
// (useBillingNotes) + the lifecycle mutations + the create modal; this component
// owns presentation only — a flat table carrying the lifecycle `status` plus a
// derived `collected` badge (true when every linked invoice is fully paid). Row
// actions: ดู / issue (DRAFT only) / cancel (DRAFT|ISSUED) / พิมพ์ PDF. A billing
// note posts NO journal entry, so there is no engine/approval column. Plain
// prototype.css classes only.

export interface BillingNoteListProps {
  billingNotes: ArBillingNote[];
  onView: (bn: ArBillingNote) => void;
  /** Issue a DRAFT note (DRAFT → ISSUED). Shown only for DRAFT rows. */
  onIssue: (bn: ArBillingNote) => void;
  /** Cancel a DRAFT/ISSUED note — the route prompts for a reason. */
  onCancel: (bn: ArBillingNote) => void;
  /** Download the billing-note PDF. */
  onPrint: (bn: ArBillingNote) => void;
  /**
   * Open the receipt modal pre-filled with this note's still-outstanding
   * invoices (รับชำระจากใบวางบิล). Shown only for a note that is NOT cancelled
   * and NOT already fully collected — a cancelled/collected note has nothing
   * left to receive.
   */
  onReceive: (bn: ArBillingNote) => void;
}

// Lifecycle-status badge — same vocabulary as the other AR status cells.
function StatusBadge({ status }: { status: ArBillingNote['status'] }) {
  switch (status) {
    case 'ISSUED':
      return (
        <span className="badge positive">
          <span className="bdot" />
          วางบิลแล้ว
        </span>
      );
    case 'CANCELLED':
      return (
        <span className="badge negative">
          <span className="bdot" />
          ยกเลิก
        </span>
      );
    default:
      return (
        <span className="badge info">
          <span className="bdot" />
          ร่าง
        </span>
      );
  }
}

export function BillingNoteList({
  billingNotes,
  onView,
  onIssue,
  onCancel,
  onPrint,
  onReceive,
}: BillingNoteListProps) {
  return (
    <div className="tbl-shell">
      <div className="tbl-wrap" style={{ maxHeight: 600 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>เลขที่ใบวางบิล</th>
              <th>ลูกค้า</th>
              <th>วันที่วางบิล</th>
              <th>วันนัดรับเช็ค</th>
              <th className="num">ยอดรวม</th>
              <th>สถานะ</th>
              <th style={{ width: 1 }} />
            </tr>
          </thead>
          <tbody>
            {billingNotes.length === 0 ? (
              <tr>
                <td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  ไม่มีข้อมูล
                </td>
              </tr>
            ) : (
              billingNotes.map((bn) => (
                <tr key={bn.id}>
                  <td>
                    <span className="code">{bn.voucherNo}</span>
                  </td>
                  <td>{bn.customerName ?? bn.customerId}</td>
                  <td>{fmtDate(bn.billingDate)}</td>
                  <td>{fmtDate(bn.dueDate)}</td>
                  <td className="num tnum">{fmt(toNumber(bn.totalAmount), 2)}</td>
                  <td>
                    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                      <StatusBadge status={bn.status} />
                      {bn.collected && bn.status !== 'CANCELLED' && (
                        <span className="badge positive" title="เก็บเงินครบทุกใบแล้ว">
                          <span className="bdot" />
                          เก็บครบแล้ว
                        </span>
                      )}
                    </span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'inline-flex', gap: 6 }}>
                      {bn.status !== 'CANCELLED' && !bn.collected && (
                        <button
                          className="btn ghost"
                          style={{ padding: '2px 8px', fontSize: 12 }}
                          onClick={() => onReceive(bn)}
                          title="รับชำระจากใบวางบิล (เปิดหน้าจอรับชำระพร้อมใบแจ้งหนี้ของใบวางบิลนี้)"
                        >
                          <Icon name="ar" size={12} /> รับชำระ
                        </button>
                      )}
                      <button
                        className="btn ghost"
                        style={{ padding: '2px 8px', fontSize: 12 }}
                        onClick={() => onView(bn)}
                        title="ดูรายละเอียด"
                      >
                        <Icon name="eye" size={12} /> ดู
                      </button>
                      {bn.status === 'DRAFT' && (
                        <button
                          className="btn primary"
                          style={{ padding: '2px 8px', fontSize: 12 }}
                          onClick={() => onIssue(bn)}
                          title="ออกใบวางบิล (ร่าง → วางบิลแล้ว)"
                        >
                          <Icon name="check" size={12} /> ออกใบวางบิล
                        </button>
                      )}
                      {(bn.status === 'DRAFT' || bn.status === 'ISSUED') && (
                        <button
                          className="btn ghost"
                          style={{ padding: '2px 8px', fontSize: 12 }}
                          onClick={() => onCancel(bn)}
                          title="ยกเลิกใบวางบิล"
                        >
                          <Icon name="x" size={12} /> ยกเลิก
                        </button>
                      )}
                      <button
                        className="btn ghost"
                        style={{ padding: '2px 8px', fontSize: 12 }}
                        onClick={() => onPrint(bn)}
                        title="พิมพ์ใบวางบิล (PDF)"
                      >
                        <Icon name="print" size={12} /> พิมพ์ PDF
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
